import { badgeClasses, type BadgeProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { COLORS_SCHEMA } from '../../palette';

// NEW VARIANT
declare module '@mui/material/Badge' {
  interface BadgeOwnProps {
    size?: 6 | 16 | 20 | 22;
  }
  interface BadgePropsVariantOverrides {
    alway: true;
    busy: true;
    online: true;
    offline: true;
    invisible: true;
    soft: true;
    filled: true;
    outlined: true;
    ghost: true;
  }
}

export default function Badge(theme: Theme) {
  return {
    MuiBadge: {
      styleOverrides: {
        dot: {
          borderRadius: '50%',
        },
        root: ({ ownerState }: { ownerState: BadgeProps }) => {
          const dot = ownerState.variant === 'dot';

          const alway = ownerState.variant === 'alway';

          const online = ownerState.variant === 'online';

          const busy = ownerState.variant === 'busy';

          const offline = ownerState.variant === 'offline';

          const invisible = ownerState.variant === 'invisible';

          const soft = ownerState.variant === 'soft';

          const filled = ownerState.variant === 'filled';

          const outlined = ownerState.variant === 'outlined';

          const ghost = ownerState.variant === 'ghost';

          const size =
            dot || alway || online || busy || offline || invisible
              ? 6
              : ownerState.size ?? 22;

          ownerState.color = ownerState.color || 'primary';

          const baseStyles = {
            [`&.${badgeClasses.invisible}`]: {
              transform: 'unset',
            },
            zIndex: 9,
            padding: 0,
            minWidth: 'auto',
            '&:before, &:after': {
              content: "''",
              borderRadius: 1,
              backgroundColor: theme.palette.common.white,
            },
          };

          const sizeStyles = {
            height: size,
            width: size,
            minWidth: size,
            fontSize: theme.typography.pxToRem(size < 20 ? 10 : 12),
            fontWeight: theme.typography.fontWeightBold,
          };

          const anchorStyle = {
            ...(ownerState.anchorOrigin?.vertical === 'top' && {
              top: '10%',
            }),
            ...(ownerState.anchorOrigin?.vertical === 'bottom' && {
              bottom: '10%',
            }),
            ...(ownerState.anchorOrigin?.horizontal === 'left' && {
              left: '10%',
            }),
            ...(ownerState.anchorOrigin?.horizontal === 'right' && {
              right: '10%',
            }),
          };

          const colorStyle = Object.values(COLORS_SCHEMA).map((color) => ({
            ...(ownerState.color === color && {
              ...(soft && {
                backgroundColor:
                  color === 'default'
                    ? theme.palette.grey[300]
                    : theme.palette[color][100],
                boxShadow: `0 0 0 1px ${theme.palette.common.white}`,
                color: theme.palette[color][600],
              }),
              ...(filled && {
                backgroundColor: theme.palette[color][500],
                boxShadow: `0 0 0 1px ${theme.palette.common.white}`,
                color: theme.palette[color].contrastText,
              }),
              ...(outlined && {
                backgroundColor: theme.palette.common.white,
                boxShadow: `0 0 0 1px ${theme.palette[color][600]}`,
                color: theme.palette[color][600],
              }),
              ...(ghost && {
                backgroundColor: 'transparent',
                color: theme.palette[color][600],
              }),
            }),
          }));

          return {
            [`&.${badgeClasses.root} > .${badgeClasses.badge}`]: {
              ...anchorStyle,
              ...sizeStyles,
              ...colorStyle.reduce((acc, style) => ({ ...acc, ...style }), {}),
              padding: 0,
              // boxShadow: '0 0 0 1px white',
            },

            ...(online && {
              [`& .${badgeClasses.badge}`]: {
                ...baseStyles,
                backgroundColor: theme.palette.success.main,
              },
            }),
            ...(busy && {
              [`& .${badgeClasses.badge}`]: {
                ...baseStyles,
                backgroundColor: theme.palette.error[500],
                '&:before': { width: 6, height: 2 },
              },
            }),
            ...(offline && {
              [`& .${badgeClasses.badge}`]: {
                ...baseStyles,
                backgroundColor: theme.palette.text.disabled,
                '&:before': {
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                },
              },
            }),
            ...(alway && {
              [`& .${badgeClasses.badge}`]: {
                ...baseStyles,
                backgroundColor: theme.palette.warning[500],
                '&:before': {
                  width: 2,
                  height: 4,
                  transform: 'translateX(1px) translateY(-1px)',
                },
                '&:after': {
                  width: 2,
                  height: 4,
                  transform: 'translateY(1px) rotate(125deg)',
                },
              },
            }),
            ...(invisible && {
              [`& .${badgeClasses.badge}`]: {
                display: 'none',
              },
            }),
          };
        },
      },
    },
  };
}
